import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Loader2, ExternalLink } from 'lucide-react';

// Journals behind one cash-flow line (direct or indirect). `fetchLineItems(section, line, method)` resolves to the API payload.
export const CashFlowLineDrilldown = ({ target, onClose, fetchLineItems, formatAmount }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!target) { setData(null); return; }
    let alive = true;
    setLoading(true); setError(null);
    fetchLineItems(target.section, target.line, target.method || 'direct')
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e.message || 'Failed to load'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [target, fetchLineItems]);

  const items = data?.items || [];
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col" data-testid="cf-line-drilldown">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-4">
            <span>{target?.label}</span>
            {data && (
              <span className={`text-base ${Number(data.total) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="cf-line-drilldown-total">
                {Number(data.total) >= 0 ? '+' : '-'}{formatAmount(Math.abs(Number(data.total || 0)))}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {data ? `${data.count} journal${data.count === 1 ? '' : 's'}${data.method === 'indirect' ? ' (signed cash effect on this line)' : ' touching bank'} · ${data.period?.start} → ${data.period?.end}` : 'Loading journals…'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto">
          {loading && <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>}
          {error && <div className="py-6 text-center text-rose-600 text-sm" data-testid="cf-line-drilldown-error">{error}</div>}
          {!loading && !error && items.length === 0 && <div className="py-10 text-center text-muted-foreground text-sm">No journals in this line for the period.</div>}
          {!loading && items.length > 0 && (
            <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[96px]">Date</TableHead>
                  <TableHead className="w-[150px]">Reference</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[220px]">{data?.method === 'indirect' ? 'Balance-sheet GLs / note' : 'GL accounts'}</TableHead>
                  <TableHead className="text-right w-[150px]">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it, idx) => (
                  <TableRow key={`${it.journal_id || it.reference}-${idx}`} data-testid={`cf-line-drilldown-row-${idx}`}>
                    <TableCell className="whitespace-nowrap text-xs">{it.date}</TableCell>
                    <TableCell className="text-xs font-mono truncate">
                      {it.journal_id ? (
                        <a href={`/reports/general-ledger?journal=${it.journal_id}`} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`cf-line-drilldown-journal-${idx}`}>
                          {it.reference || 'Journal'} <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (it.reference || '—')}
                    </TableCell>
                    <TableCell className="text-sm truncate" title={it.description}>{it.counterparty || it.description}</TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate" title={(it.gl_names || []).join(', ')}>
                      {(it.gl_names || []).join(', ')}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums whitespace-nowrap ${it.type === 'inflow' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {it.type === 'inflow' ? '+' : '-'}{formatAmount(Math.abs(Number(it.amount || 0)))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CashFlowLineDrilldown;
